import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import LatestPatchNotesCard from '../components/LatestPatchNotesCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Wrench,
  Clock,
  CheckCircle2,
  Package,
  Building2,
  MapPin,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Play,
  Pause,
  Hand,
  CalendarClock,
  ListChecks
} from 'lucide-react';
import { Link } from 'react-router-dom';

const priorityLabels = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Media',
  bassa: 'Bassa'
};

const statusLabels = {
  aperto: 'Aperto',
  assegnato: 'Assegnato',
  in_lavorazione: 'In Lavorazione',
  in_attesa_pezzi: 'In Attesa Pezzi',
  completato: 'Completato',
  chiuso: 'Chiuso'
};

const frequencyLabels = {
  daily: 'Giornaliera',
  weekly: 'Settimanale',
  monthly: 'Mensile',
  quarterly: 'Trimestrale',
  semiannually: 'Semestrale',
  yearly: 'Annuale',
  mensile: 'Mensile',
  bimestrale: 'Bimestrale',
  trimestrale: 'Trimestrale',
  quadrimestrale: 'Quadrimestrale',
  semestrale: 'Semestrale',
  annuale: 'Annuale',
  biennale: 'Biennale',
};

const TechnicianDashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const { data: dashboardData } = await api.get('/dashboard/technician');
      setData(dashboardData);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const updateTicketStatus = async (ticketId, newStatus) => {
    try {
      setUpdating(ticketId);
      await api.put(`/tickets/${ticketId}`, { status: newStatus });
      toast.success(`Ticket ${statusLabels[newStatus].toLowerCase()}`);
      fetchDashboard();
    } catch (err) {
      toast.error('Errore nell\'aggiornamento');
    } finally {
      setUpdating(null);
    }
  };

  const takeTicket = async (ticketId) => {
    try {
      setUpdating(ticketId);
      await api.put(`/tickets/${ticketId}`, { assigned_to: user.id });
      toast.success('Ticket preso in carico!');
      fetchDashboard();
    } catch (err) {
      toast.error('Errore nella presa in carico');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="technician-loading">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  const priorityOrder = { urgente: 0, alta: 1, media: 2, bassa: 3 };
  const sortedTickets = [...(data?.active_tickets || [])].sort((a, b) => 
    priorityOrder[a.priority] - priorityOrder[b.priority]
  );

  return (
    <div className="space-y-6" data-testid="technician-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900">I Miei Lavori</h1>
          <p className="text-slate-500 mt-1">Ciao {user?.name}, ecco i tuoi ticket</p>
        </div>
        <Button onClick={fetchDashboard} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Aggiorna
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="stat-card" data-testid="stat-available">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Hand className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-slate-900">{data?.unassigned || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Disponibili</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-assigned">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-slate-900">{data?.assigned || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Da iniziare</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-in-progress">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-100 rounded-xl flex items-center justify-center">
                <Wrench className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-slate-900">{data?.in_progress || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">In corso</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-waiting">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-slate-900">{data?.waiting_parts || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">In attesa</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="stat-completed-month">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-black text-slate-900">{data?.completed_this_month || 0}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wider">Questo mese</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Manutenzioni programmate (con checklist) assegnate a questo tecnico */}
      {(data?.scheduled_maintenances || []).length > 0 && (
        <Card className="border-amber-200 bg-amber-50/30 dark:bg-amber-950/20 dark:border-amber-900" data-testid="scheduled-maintenances-card">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-amber-600" />
              Manutenzioni Programmate ({data.scheduled_maintenances.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="p-4 space-y-3">
              {data.scheduled_maintenances.map((m) => {
                const dt = m.scheduled_date ? new Date(m.scheduled_date) : null;
                const dateLabel = dt ? dt.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                return (
                  <Link
                    key={m.id}
                    to={`/scheduled/${m.id}`}
                    className="block bg-white dark:bg-slate-900 rounded-xl border border-amber-200 dark:border-amber-900 p-4 hover:shadow-md transition-shadow"
                    data-testid={`scheduled-card-${m.id}`}
                  >
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <Badge className={`priority-${m.priority} text-sm font-bold px-3 py-1`}>
                        {priorityLabels[m.priority] || m.priority}
                      </Badge>
                      {m.is_overdue ? (
                        <Badge className="bg-red-600 text-white">⚠️ Scaduta</Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-700 border-amber-300 dark:text-amber-300">
                          In arrivo
                        </Badge>
                      )}
                    </div>
                    <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 mb-1">{m.title}</h3>
                    {m.description && (
                      <p className="text-sm text-slate-600 dark:text-slate-400 mb-2 line-clamp-2">{m.description}</p>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 dark:text-slate-400 mb-2">
                      <span className="flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        {m.hotel_name}
                      </span>
                      <span className="flex items-center gap-1">
                        <CalendarClock className="w-4 h-4" />
                        {dateLabel}
                      </span>
                      {m.frequency && (
                        <span className="capitalize text-xs px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded">
                          {frequencyLabels[String(m.frequency).toLowerCase()] || m.frequency}
                        </span>
                      )}
                    </div>
                    {/* Checklist progress */}
                    {m.checklist_total > 0 && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300">
                            <ListChecks className="w-3.5 h-3.5" />
                            Checklist: {m.checklist_done}/{m.checklist_total}
                          </span>
                          <span className="font-semibold text-slate-700 dark:text-slate-200">{m.checklist_pct}%</span>
                        </div>
                        <div className="h-2 w-full bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all ${m.checklist_pct === 100 ? 'bg-green-500' : 'bg-amber-500'}`}
                            style={{ width: `${m.checklist_pct}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available tickets - can be picked up */}
      {(data?.available_tickets || []).length > 0 && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Hand className="w-5 h-5 text-blue-600" />
              Ticket Disponibili ({data.available_tickets.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="p-4 space-y-4">
              {data.available_tickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className="bg-white rounded-xl border border-blue-200 p-4"
                  data-testid={`available-card-${ticket.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <Badge className={`priority-${ticket.priority} text-sm font-bold px-3 py-1`}>
                      {ticket.priority === 'urgente' && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {priorityLabels[ticket.priority]}
                    </Badge>
                    <Badge variant="secondary" className="status-aperto">Aperto</Badge>
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{ticket.title}</h3>
                  <p className="text-sm text-slate-600 mb-3 line-clamp-2">{ticket.description}</p>
                  <div className="flex flex-wrap gap-3 mb-4 text-sm">
                    <span className="flex items-center gap-1 text-slate-500">
                      <Building2 className="w-4 h-4" />
                      {ticket.hotel_name}
                    </span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <MapPin className="w-4 h-4" />
                      {ticket.area}
                    </span>
                  </div>
                  <Button
                    className="w-full h-12 text-base font-bold bg-blue-600 hover:bg-blue-700"
                    onClick={() => takeTicket(ticket.id)}
                    disabled={updating === ticket.id}
                    data-testid={`take-button-${ticket.id}`}
                  >
                    {updating === ticket.id ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Hand className="w-5 h-5 mr-2" />
                        Prendi in Carico
                      </>
                    )}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active tickets - Mobile-first job cards */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold">Lavori Attivi</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[calc(100vh-400px)]">
            <div className="p-4 space-y-4">
              {sortedTickets.map((ticket) => (
                <div
                  key={ticket.id}
                  className={`job-card priority-${ticket.priority}`}
                  data-testid={`job-card-${ticket.id}`}
                >
                  {/* Priority & Status badges */}
                  <div className="flex items-center justify-between mb-3">
                    <Badge className={`priority-${ticket.priority} text-sm font-bold px-3 py-1`}>
                      {ticket.priority === 'urgente' && <AlertTriangle className="w-3 h-3 mr-1" />}
                      {priorityLabels[ticket.priority]}
                    </Badge>
                    <Badge variant="secondary" className={`status-${ticket.status}`}>
                      {statusLabels[ticket.status]}
                    </Badge>
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{ticket.title}</h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{ticket.description}</p>

                  {/* Location info */}
                  <div className="flex flex-wrap gap-3 mb-4 text-sm">
                    <span className="flex items-center gap-1 text-slate-500">
                      <Building2 className="w-4 h-4" />
                      {ticket.hotel_name}
                    </span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <MapPin className="w-4 h-4" />
                      {ticket.area}
                    </span>
                  </div>

                  {/* Quick action buttons */}
                  <div className="grid grid-cols-2 gap-3">
                    {ticket.status === 'assegnato' && (
                      <Button
                        className="h-14 text-base font-bold bg-cyan-600 hover:bg-cyan-700"
                        onClick={() => updateTicketStatus(ticket.id, 'in_lavorazione')}
                        disabled={updating === ticket.id}
                        data-testid={`start-button-${ticket.id}`}
                      >
                        {updating === ticket.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Play className="w-5 h-5 mr-2" />
                            Inizia
                          </>
                        )}
                      </Button>
                    )}

                    {ticket.status === 'in_lavorazione' && (
                      <>
                        <Button
                          variant="outline"
                          className="h-14 text-base font-bold border-violet-500 text-violet-600 hover:bg-violet-50"
                          onClick={() => updateTicketStatus(ticket.id, 'in_attesa_pezzi')}
                          disabled={updating === ticket.id}
                          data-testid={`wait-button-${ticket.id}`}
                        >
                          <Pause className="w-5 h-5 mr-2" />
                          In Attesa
                        </Button>
                        <Button
                          className="h-14 text-base font-bold bg-green-600 hover:bg-green-700"
                          onClick={() => updateTicketStatus(ticket.id, 'completato')}
                          disabled={updating === ticket.id}
                          data-testid={`complete-button-${ticket.id}`}
                        >
                          {updating === ticket.id ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              <CheckCircle2 className="w-5 h-5 mr-2" />
                              Completato
                            </>
                          )}
                        </Button>
                      </>
                    )}

                    {ticket.status === 'in_attesa_pezzi' && (
                      <>
                        <Button
                          className="h-14 text-base font-bold bg-cyan-600 hover:bg-cyan-700"
                          onClick={() => updateTicketStatus(ticket.id, 'in_lavorazione')}
                          disabled={updating === ticket.id}
                          data-testid={`resume-button-${ticket.id}`}
                        >
                          <Play className="w-5 h-5 mr-2" />
                          Riprendi
                        </Button>
                        <Button
                          className="h-14 text-base font-bold bg-green-600 hover:bg-green-700"
                          onClick={() => updateTicketStatus(ticket.id, 'completato')}
                          disabled={updating === ticket.id}
                          data-testid={`complete-wait-button-${ticket.id}`}
                        >
                          <CheckCircle2 className="w-5 h-5 mr-2" />
                          Completato
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              ))}

              {sortedTickets.length === 0 && (
                <div className="text-center py-12">
                  <CheckCircle2 className="w-16 h-16 text-green-300 mx-auto mb-4" />
                  <p className="text-lg font-medium text-slate-600">Nessun lavoro in corso</p>
                  <p className="text-sm text-slate-400 mt-1">Ottimo lavoro!</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>

      <LatestPatchNotesCard limit={3} />
    </div>
  );
};

export default TechnicianDashboard;
